import { defineComponent as _defineComponent } from 'vue';
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, setBlockTracking as _setBlockTracking, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { class: "pb-[100%] relative" };
const _hoisted_3 = { class: "absolute inset-0 flex justify-center items-center skeleton-shimmer" };
const _hoisted_4 = {
    key: 1,
    class: "absolute top-2 left-2 px-2 py-1 bg-white rounded-[4px] text-badge z-1 text-sm drop-shadow-lg"
};
const _hoisted_5 = {
    key: 2,
    class: "absolute top-2 left-2 px-2 py-1 bg-badge rounded-[4px] text-white z-1 text-sm drop-shadow-lg font-bold"
};
const _hoisted_6 = ["src"];
const _hoisted_7 = { class: "flex justify-between items-center w-full" };
const _hoisted_8 = { key: 1 };
const _hoisted_9 = { class: "flex justify-start items-center flex-none" };
const _hoisted_10 = { class: "flex flex-wrap gap-1 mt-1 mb-2 min-h-[25px]" };
import { computed } from "vue";
import { useHomeStore } from "./homeStore";
import { CheckCircle, Circle, } from "lucide-vue-next";
export default /*@__PURE__*/ _defineComponent({
    __name: 'market-tab-item',
    props: {
        data: {}
    },
    setup(__props) {
        const homeStore = useHomeStore();
        // Define props
        const props = __props;
        // MARK: Functions
        const itemTapped = () => {
            if (homeStore.itemInSelectionView) {
                if (isItemSelected.value) {
                    homeStore.selectedItems = homeStore.selectedItems.filter((item) => item != props.data);
                }
                else {
                    homeStore.selectedItems.push(props.data);
                }
                homeStore.selectedItems.sort((a, b) => b.id - a.id);
            }
            else {
                window.location.href = productUrl.value;
            }
        };
        const onRequest = computed(() => {
            return props.data.on_request ?? false;
        });
        // Computed properties - Core data (calculate once, reuse multiple times)
        const isSold = computed(() => {
            return props.data.sold ?? false;
        });
        const productId = computed(() => `market-product-${props.data.id}`);
        const imageUrl = computed(() => {
            if (props.data.images) {
                const firstImage = props.data.images.split(";")[0];
                return `/listing_images/${firstImage}`;
            }
            return "/assets/icons/watch.png";
        });
        const productUrl = computed(() => `/listing/${props.data.id}`);
        const truncatedTitle = computed(() => {
            if (props.data.type === "Buy") {
                return props.data.title ?? "";
            }
            // Optimized string concatenation - build array then join (faster than multiple +=)
            const parts = [props.data.brand ?? ""];
            if (props.data.model)
                parts.push(` ${props.data.model}`);
            if (props.data.model_series)
                parts.push(` ${props.data.model_series}`);
            if (props.data.size_diameter) {
                parts.push(` - ${props.data.size_diameter.replace(/\s/g, "")}`);
            }
            return parts.join("").replace(/[\\\"']/g, "");
        });
        const formattedPrice = computed(() => {
            return new Intl.NumberFormat().format(props.data.price || 0);
        });
        const statusText = computed(() => (isSold.value ? "Sold" : "Active"));
        const badgeItems = computed(() => {
            const items = [];
            // Add item_condition badges (split by semicolon)
            if (props.data.item_condition) {
                const conditions = props.data.item_condition
                    .split(";")
                    .map((c) => c.trim())
                    .filter(Boolean); // Simpler and faster than c !== ''
                items.push(...conditions);
            }
            // Add accessories badge (single value)
            if (props.data.accessories) {
                items.push(props.data.accessories);
            }
            return items;
        });
        // Computed classes - Avoid repeated ternary operations in template (better performance)
        // flex-col is for grid view
        const itemClasses = computed(() => [
            "size-full flex relative cursor-pointer",
            homeStore.itemInGridView ? "flex-col" : "",
        ]);
        const imageClasses = computed(() => [
            "rounded-[8px] overflow-hidden relative",
            homeStore.itemInGridView ? "w-full " : "w-1/3",
        ]);
        const descClasses = computed(() => [
            homeStore.itemInGridView ? "w-full flex-none " : "flex-1 pl-3",
        ]);
        const titleClasses = computed(() => [
            "text-sm mt-2 mb-1 font-medium text-ellipsis line-clamp-2 h-[34px]",
            isSold.value ? "text-hint" : "text-black",
        ]);
        const priceClasses = computed(() => [
            "text-base flex-1",
            isSold.value ? "text-hint" : "text-black",
        ]);
        const statusDotClasses = computed(() => [
            "size-[8px] rounded-full mr-1",
            isSold.value ? "bg-red-500" : "bg-green-500",
        ]);
        const statusTextClasses = computed(() => [
            "text-sm",
            isSold.value ? "text-red-500" : "text-hint",
        ]);
        const badgeClasses = computed(() => [
            "px-2 py-1 bg-secondary2 rounded-[4px] text-badge text-sm",
            isSold.value ? "opacity-50" : "",
        ]);
        const isInSelectionView = computed(() => homeStore.itemInSelectionView);
        const isItemSelected = computed(() => homeStore.itemInSelectionView &&
            homeStore.selectedItems.findIndex((item) => item == props.data) != -1);
        const handleImageError = (event) => {
            const target = event.target;
            if (!target || target.dataset.fallbackApplied)
                return;
            target.dataset.fallbackApplied = "true";
            target.style.display = "none";
        };
        const frameClasses = computed(() => [
            "absolute z-10 size-full rounded-[8px]",
            isItemSelected.value ? "border-4 border-indicator overflow-hidden" : "",
        ]);
        const itemSelectionClasses = computed(() => [
            isItemSelected.value ? "absolute right-2 z-1 text-sm rounded-full bg-white/30 text-indicator" : "absolute right-2 z-1 text-sm rounded-full bg-black/70 text-white/30",
            homeStore.itemInGridView ? "top-2" : "bottom-2",
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(itemClasses.value),
                id: productId.value,
                onClick: _cache[2] || (_cache[2] = ($event) => (itemTapped()))
            }, [
                _createElementVNode("div", {
                    class: _normalizeClass(imageClasses.value)
                }, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            (isInSelectionView.value)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: _normalizeClass(frameClasses.value)
                                }, null, 2))
                                : _createCommentVNode("", true),
                            _cache[0] || (_setBlockTracking(-1, true),
                                (_cache[0] = (__props.data.status == '3')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[3] || (_cache[3] = [
                                            _createTextVNode(" Verified ", -1)
                                        ]))]))
                                    : _createCommentVNode("", true)).cacheIndex = 0,
                                _setBlockTracking(1),
                                _cache[0]),
                            _cache[1] || (_setBlockTracking(-1, true),
                                (_cache[1] = (__props.data.status == '4')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[4] || (_cache[4] = [
                                            _createTextVNode(" On Hold ", -1)
                                        ]))]))
                                    : _createCommentVNode("", true)).cacheIndex = 1,
                                _setBlockTracking(1),
                                _cache[1]),
                            _createElementVNode("img", {
                                src: imageUrl.value,
                                class: "lozad size-full object-cover",
                                onError: handleImageError
                            }, null, 40, _hoisted_6),
                            (isInSelectionView.value)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 3,
                                    class: _normalizeClass(itemSelectionClasses.value)
                                }, [
                                    (isItemSelected.value)
                                        ? (_openBlock(), _createBlock(_unref(CheckCircle), { key: 0 }))
                                        : (_openBlock(), _createBlock(_unref(Circle), { key: 1 }))
                                ], 2))
                                : _createCommentVNode("", true)
                        ])
                    ])
                ], 2),
                _createElementVNode("div", {
                    class: _normalizeClass(descClasses.value)
                }, [
                    _createElementVNode("div", {
                        class: _normalizeClass(titleClasses.value)
                    }, _toDisplayString(truncatedTitle.value), 3),
                    _createElementVNode("div", _hoisted_7, [
                        (!onRequest.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(priceClasses.value)
                            }, "RM " + _toDisplayString(formattedPrice.value), 3))
                            : (_openBlock(), _createElementBlock("i", _hoisted_8, "Price on Request")),
                        _createElementVNode("div", _hoisted_9, [
                            _createElementVNode("div", {
                                class: _normalizeClass(statusDotClasses.value)
                            }, null, 2),
                            _createElementVNode("div", {
                                class: _normalizeClass(statusTextClasses.value)
                            }, _toDisplayString(statusText.value), 3)
                        ])
                    ]),
                    _createElementVNode("div", _hoisted_10, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(badgeItems.value, (item, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: `badge-${index}`,
                                class: _normalizeClass(badgeClasses.value)
                            }, _toDisplayString(item), 3));
                        }), 128))
                    ])
                ], 2)
            ], 10, _hoisted_1));
        };
    }
});
